import type { Locale } from '@/i18n/config'

/**
 * 公开页（营销面）路由清单 — App.tsx 的无鉴权路由、AuthGate 的 refresh 跳过、
 * 公开页内互链（PublicLink）都以此为准。英文版 = 同一批公开页加 /en 前缀，
 * 语言由 URL 决定（/en/* 强制 en）— 这是 hreflang / og:locale:alternate 声明
 * 能成立的前提：爬虫看到的静态 HTML 与 URL 一一对应。
 *
 * 控制台路由不在此列：语言跟随用户偏好（stores/locale），URL 不携带语言。
 */
export const PUBLIC_PATHS = ['/', '/login', '/register', '/privacy', '/terms', '/docs', '/calculator'] as const

export const EN_PREFIX = '/en'

export function isEnPublicPath(pathname: string): boolean {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`)
}

export function isPublicPath(pathname: string): boolean {
  return (PUBLIC_PATHS as readonly string[]).includes(pathname) || isEnPublicPath(pathname)
}

/** URL 声明的语言：/en/* → en；其余（zh 公开页与控制台）→ null，由用户偏好决定。 */
export function urlLocale(pathname: string): Locale | null {
  return isEnPublicPath(pathname) ? 'en' : null
}

/** zh 公开路径 → 英文变体（/ → /en，/login → /en/login）。仅对 PUBLIC_PATHS 成员有意义。 */
export function toEnPath(pathname: string): string {
  return pathname === '/' ? EN_PREFIX : `${EN_PREFIX}${pathname}`
}

/** 英文变体 → zh 路径（/en → /，/en/login → /login）。 */
export function toZhPath(pathname: string): string {
  return pathname === EN_PREFIX ? '/' : pathname.slice(EN_PREFIX.length)
}

/**
 * 公开页内跳转的本地化目标：当前处于 /en/* 时，公开目标映射到英文变体；
 * 控制台目标（/dashboard 等）与非公开页一律原样返回。
 */
export function localizedPublicPath(currentPathname: string, target: string): string {
  if (!(PUBLIC_PATHS as readonly string[]).includes(target)) return target
  return isEnPublicPath(currentPathname) ? toEnPath(target) : target
}
